import { TEACHER_WORKFLOW_IDS } from "../config/teacherWorkflows";

export const SENSAI_EMBED_MODE = "td-sensai";
export const TD_PORTAL_EMBED_MODE = "td-portal";
export const SENSAI_EMBED_SOURCE = "td";
export const SENSAI_MESSAGE_SOURCE = "csd";
export const SENSAI_MESSAGE_TARGET = "td-sensai";

export const SENSAI_EMBEDDED_WORKFLOW_ALLOWLIST = TEACHER_WORKFLOW_IDS;

const LOCAL_TD_ALLOWED_ORIGINS = [
  "http://localhost:3000",
  "http://localhost:3002"
];

const splitConfiguredOrigins = (value) =>
  String(value || "")
    .split(",")
    .map((origin) => origin.trim())
    .filter(Boolean);

const decodeBase64Url = (encodedValue) => {
  const base64 = encodedValue
    .replace(/-/g, "+")
    .replace(/_/g, "/")
    .padEnd(Math.ceil(encodedValue.length / 4) * 4, "=");
  const binary = window.atob(base64);
  const escaped = Array.from(binary, (character) =>
    `%${character.charCodeAt(0).toString(16).padStart(2, "0")}`
  ).join("");

  return decodeURIComponent(escaped);
};

export const parseWorkflowContext = (encodedContext) => {
  if (!encodedContext) {
    return {};
  }

  const parseAttempts = [
    () => JSON.parse(encodedContext),
    () => JSON.parse(decodeBase64Url(encodedContext))
  ];

  for (const parseAttempt of parseAttempts) {
    try {
      const parsedContext = parseAttempt();
      return parsedContext && typeof parsedContext === "object" ? parsedContext : {};
    } catch (error) {
      // Try the next supported context format.
    }
  }

  return {};
};

const firstPresentValue = (...values) =>
  values.find(
    (value) =>
      value !== undefined && value !== null && String(value).trim() !== ""
  ) || "";

export const getWorkflowLaunchMetadata = (searchParams) => {
  const parsedContext = parseWorkflowContext(searchParams.get("context"));

  return {
    workflow: firstPresentValue(searchParams.get("workflow"), parsedContext.workflow),
    source: firstPresentValue(searchParams.get("source"), parsedContext.source),
    embed: firstPresentValue(searchParams.get("embed"), parsedContext.embed),
    launchId: firstPresentValue(searchParams.get("launchId"), parsedContext.launchId),
    parentOrigin: firstPresentValue(
      searchParams.get("parentOrigin"),
      parsedContext.parentOrigin
    ),
    parsedContext
  };
};

export const normaliseOrigin = (value) => {
  const candidate = String(value || "").trim().replace(/^['"]|['"]$/g, "");

  if (!candidate) {
    return "";
  }

  try {
    return new URL(candidate).origin;
  } catch (error) {
    return "";
  }
};

export const getAllowedTeacherDashboardOrigins = () => {
  const allowLocalOrigins = ["development", "test"].includes(
    process.env.NODE_ENV
  );
  const configuredOrigins = [
    ...splitConfiguredOrigins(process.env.REACT_APP_TD_ALLOWED_ORIGINS),
    process.env.REACT_APP_TD_URL,
    process.env.REACT_APP_TD_ADMIN_DASHBOARD
  ];

  return Array.from(
    new Set(
      [
        ...(allowLocalOrigins ? LOCAL_TD_ALLOWED_ORIGINS : []),
        ...configuredOrigins
      ]
        .map(normaliseOrigin)
        .filter(Boolean)
    )
  );
};

export const resolveAllowedParentOrigin = (...candidates) => {
  const allowedOrigins = new Set(getAllowedTeacherDashboardOrigins());

  return candidates
    .map(normaliseOrigin)
    .find((origin) => origin && allowedOrigins.has(origin)) || "";
};

export const buildSensaiWorkflowMessage = ({
  type,
  workflow,
  launchId,
  status = "",
  reference = "",
  message = ""
}) => ({
  source: SENSAI_MESSAGE_SOURCE,
  target: SENSAI_MESSAGE_TARGET,
  type,
  workflow,
  launchId,
  status,
  reference,
  message
});

export const postSensaiWorkflowMessage = ({
  parentWindow,
  targetOrigin,
  type,
  workflow,
  launchId,
  status,
  reference,
  message
}) => {
  if (!parentWindow || !targetOrigin || !type || !workflow || !launchId) {
    return false;
  }

  parentWindow.postMessage(
    buildSensaiWorkflowMessage({
      type,
      workflow,
      launchId,
      status,
      reference,
      message
    }),
    targetOrigin
  );

  return true;
};
